/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, Output} from '@angular/core';

import {deferMarkForCheck} from '../util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-copy-to-clipboard-button',
  template: `
    <button type="button" [class]="buttonClass" (click)="copyText()">
      <span class="fa" [ngClass]="copied ? 'fa-check' : iconClass"></span><span *ngIf="displayLabel"> {{ displayLabel }}</span>
    </button>
  `
})
export class CopyToClipboardButtonComponent implements OnDestroy {
  @Input() text = '';
  @Input() label = 'Copy';
  @Input() copiedLabel = 'Copied';
  @Input() iconClass = 'fa-clipboard';
  @Input() buttonClass = 'btn btn-default btn-xs';
  @Output() copiedChange = new EventEmitter<boolean>();

  copied = false;
  private copiedTimer: any = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnDestroy(): void {
    clearTimeout(this.copiedTimer);
  }

  get displayLabel(): string {
    if (!this.copied) {
      return this.label;
    }
    return this.copiedLabel || (this.label ? 'Copied' : '');
  }

  async copyText(): Promise<void> {
    const copied = await this.writeText(this.text || '');
    if (!copied) {
      return;
    }

    // Clipboard settles as a microtask during the click's zoneless tick.
    // Flipping `copied` then races checkNoChanges (NG0100: fa-copy → fa-check).
    deferMarkForCheck(this.cdr, () => {
      this.copied = true;
      this.copiedChange.emit(true);
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        deferMarkForCheck(this.cdr, () => {
          this.copied = false;
          this.copiedChange.emit(false);
        });
      }, 1500);
    });
  }

  private async writeText(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_error) {
      // Fall through to the execCommand fallback for older browsers.
    }

    const textArea = this.document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', 'readonly');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    this.document.body.appendChild(textArea);
    const activeElement = this.document.activeElement as HTMLElement | null;
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);

    let copied = false;
    try {
      copied = this.document.execCommand('copy');
    } catch (_error) {
      copied = false;
    }

    if (activeElement?.focus) {
      activeElement.focus();
    }
    this.document.body.removeChild(textArea);
    return copied;
  }
}
